import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ChatData, ChatMessage, ChatParticipant } from "../../types/global.d";
import { MAX_MESSAGE_LENGTH, UserStatus } from "../../constants";
import { getChatById, sendMessage as apiSendMessage } from "../../api/chats";
import { useUserContext } from "../../contexts/UserContext";
import { useChatContext } from "../../contexts/ChatContext";
import { interpolateSystemMessage } from "../../utils/messageUtils";
import Avatar from "../common/Avatar";
import "../styles/ChatModal.css";

interface ChatModalProps {
  isOpen: boolean;
  mode: "list" | "thread";
  chatId?: string;
  nickname?: string;
  requestKey: number;
  onClose: () => void;
}

const getMessageTime = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString();
};

const sanitizeMessage = (text: string): string => {
  return text.replace(/\n{3,}/g, "\n\n");
};

const getDisplayParticipant = (
  chat: ChatData,
  currentNickname?: string
): ChatParticipant | undefined => {
  const participants = chat.participants || [];
  return (
    participants.find(
      (participant) =>
        participant.status !== UserStatus.virtual &&
        participant.nickname !== currentNickname
    ) ||
    participants.find((participant) => participant.status !== UserStatus.virtual) ||
    participants[0]
  );
};

const isMessageSentByViewer = (
  message: ChatMessage,
  currentUserId?: string
): boolean => {
  if (currentUserId && message.sender?._id === currentUserId) {
    return true;
  }

  return message.data?.sentByAdmin === currentUserId;
};

const hasUnreadForViewer = (
  chat: ChatData,
  currentUserId?: string
): boolean => {
  if ((chat.unreadCount || 0) > 0) {
    return true;
  }

  return (chat.messages || []).some((message) => {
    if (message.read || isMessageSentByViewer(message, currentUserId)) {
      return false;
    }

    return message.sender?.status !== UserStatus.virtual;
  });
};

const ChatModal: React.FC<ChatModalProps> = memo(
  ({ isOpen, mode, chatId, nickname, requestKey, onClose }) => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const {
      chats,
      loading,
      error,
      refreshChats,
      ensureChatWithNickname,
      upsertChat,
      markChatAsRead,
    } = useChatContext();
    const [view, setView] = useState<"list" | "thread">(mode);
    const [activeChat, setActiveChat] = useState<ChatData | null>(null);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState<string | null>(null);
    const [messageText, setMessageText] = useState("");
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const initializedRequestKeyRef = useRef<number | null>(null);

    const loadChatById = useCallback(
      async (id: string) => {
        setView("thread");
        setThreadLoading(true);
        setThreadError(null);
        setMessageText("");

        try {
          const chat = await getChatById(id);
          let nextChat = chat;

          if (hasUnreadForViewer(chat, user?._id)) {
            await markChatAsRead(chat._id);
            nextChat = {
              ...chat,
              unreadCount: 0,
              messages: (chat.messages || []).map((message) => ({
                ...message,
                read: true,
              })),
            };
          }

          setActiveChat(nextChat);
          upsertChat(nextChat);
        } catch (err: any) {
          setThreadError(err?.message || t("chat.failedToLoadChat"));
        } finally {
          setThreadLoading(false);
        }
      },
      [markChatAsRead, t, upsertChat, user?._id]
    );

    const openChatForNickname = useCallback(
      async (targetNickname: string) => {
        setView("thread");
        setThreadLoading(true);
        setThreadError(null);
        setMessageText("");

        try {
          const chat = await ensureChatWithNickname(targetNickname);
          if (hasUnreadForViewer(chat, user?._id)) {
            await markChatAsRead(chat._id);
          }
          const normalizedChat = {
            ...chat,
            unreadCount: 0,
            messages: (chat.messages || []).map((message) => ({
              ...message,
              read: true,
            })),
          };
          setActiveChat(normalizedChat);
          upsertChat(normalizedChat);
          await refreshChats();
        } catch (err: any) {
          setThreadError(err?.message || t("chat.failedToOpenChat"));
        } finally {
          setThreadLoading(false);
        }
      },
      [
        ensureChatWithNickname,
        markChatAsRead,
        refreshChats,
        t,
        upsertChat,
        user?._id,
      ]
    );

    useEffect(() => {
      if (!isOpen) {
        initializedRequestKeyRef.current = null;
        return;
      }

      if (initializedRequestKeyRef.current === requestKey) {
        return;
      }

      initializedRequestKeyRef.current = requestKey;

      if (mode === "thread" && nickname) {
        openChatForNickname(nickname);
        return;
      }

      if (mode === "thread" && chatId) {
        loadChatById(chatId);
        return;
      }

      setView("list");
      setActiveChat(null);
      setThreadError(null);
      setMessageText("");
      refreshChats();
    }, [
      chatId,
      isOpen,
      loadChatById,
      mode,
      nickname,
      openChatForNickname,
      refreshChats,
      requestKey,
    ]);

    useEffect(() => {
      if (!isOpen || view !== "thread") {
        return;
      }

      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [activeChat?.messages?.length, isOpen, view]);

    const activeParticipant = useMemo(() => {
      return activeChat
        ? getDisplayParticipant(activeChat, user?.nickname)
        : undefined;
    }, [activeChat, user?.nickname]);

    const isOwnMessage = useCallback(
      (message: ChatMessage) => {
        return isMessageSentByViewer(message, user?._id);
      },
      [user?._id]
    );

    const getPreviewText = useCallback(
      (chat: ChatData) => {
        const message =
          chat.lastMessagePreview ||
          chat.messages?.[chat.messages.length - 1] ||
          null;

        if (!message) {
          return t("chat.noMessage");
        }

        if (message.messageType === "system") {
          return interpolateSystemMessage(message.content, message.data, t);
        }

        return message.content;
      },
      [t]
    );

    const handleOverlayClick = useCallback(
      (event: React.MouseEvent) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      },
      [onClose]
    );

    const handlePanelClick = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
    }, []);

    const handleBackToList = useCallback(() => {
      setView("list");
      setActiveChat(null);
      setThreadError(null);
      setMessageText("");
      refreshChats();
    }, [refreshChats]);

    const handleOpenChat = useCallback(
      (chat: ChatData) => {
        loadChatById(chat._id);
      },
      [loadChatById]
    );

    const handleMessageChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessageText(sanitizeMessage(event.target.value));
      },
      []
    );

    const handleSendMessage = useCallback(async () => {
      if (!activeChat || sending) {
        return;
      }

      const trimmedText = sanitizeMessage(messageText).trim();
      if (!trimmedText || trimmedText.length > MAX_MESSAGE_LENGTH) {
        return;
      }

      setSending(true);
      setThreadError(null);
      try {
        const message = await apiSendMessage(activeChat._id, trimmedText);
        const safeMessage = {
          ...message,
          read: true,
        };
        const nextChat = {
          ...activeChat,
          messages: [...(activeChat.messages || []), safeMessage],
          lastMessage: new Date().toISOString(),
          lastMessagePreview: safeMessage,
          unreadCount: 0,
        };

        setActiveChat(nextChat);
        upsertChat(nextChat);
        setMessageText("");
        await refreshChats();
      } catch (err: any) {
        setThreadError(err?.message || t("chat.failedToSendMessage"));
      } finally {
        setSending(false);
      }
    }, [activeChat, messageText, refreshChats, sending, t, upsertChat]);

    const handleComposerKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleSendMessage();
        }
      },
      [handleSendMessage]
    );

    if (!isOpen) {
      return null;
    }

    const renderList = () => (
      <div className="chat-body chat-list-view">
        {loading && chats.length === 0 ? (
          <div className="chat-state">{t("chat.loadingChats")}</div>
        ) : error && chats.length === 0 ? (
          <div className="chat-state chat-state-error">
            <p>{error}</p>
            <button type="button" onClick={refreshChats}>
              {t("chat.retry")}
            </button>
          </div>
        ) : chats.length === 0 ? (
          <div className="chat-empty-state">
            <h4>{t("chat.noChatsYet")}</h4>
            <p>{t("chat.startConversationFromProfile")}</p>
          </div>
        ) : (
          <div className="chat-list">
            {chats.map((chat) => {
              const participant = getDisplayParticipant(chat, user?.nickname);
              const unreadCount = Math.max(chat.unreadCount || 0, 0);

              return (
                <button
                  type="button"
                  key={chat._id}
                  className={`chat-list-item${
                    unreadCount > 0 ? " unread" : ""
                  }`}
                  onClick={() => handleOpenChat(chat)}
                >
                  <div className="chat-list-avatar">
                    <Avatar
                      avatar={participant?.avatar}
                      nickname={participant?.nickname || "U"}
                      size={46}
                    />
                    {unreadCount > 0 && (
                      <span className="chat-unread-badge">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="chat-list-text">
                    <div className="chat-list-title-row">
                      <span className="chat-list-name">
                        {participant?.nickname || t("chat.unknownUser")}
                      </span>
                      <span className="chat-list-time">
                        {getMessageTime(chat.lastMessage || chat.updatedAt)}
                      </span>
                    </div>
                    <span className="chat-list-preview">
                      {getPreviewText(chat)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );

    const renderThread = () => (
      <div className="chat-body chat-thread-view">
        {threadLoading && !activeChat ? (
          <div className="chat-state">{t("chat.loadingChat")}</div>
        ) : threadError && !activeChat ? (
          <div className="chat-state chat-state-error">
            <p>{threadError}</p>
            <button type="button" onClick={handleBackToList}>
              {t("chat.backToChats")}
            </button>
          </div>
        ) : (
          <>
            <div className="chat-thread-messages">
              {threadError && (
                <div className="chat-inline-error">{threadError}</div>
              )}
              {activeChat?.messages?.length ? (
                activeChat.messages.map((message) => {
                  const own = isOwnMessage(message);
                  const isSystem = message.messageType === "system";

                  return (
                    <div
                      key={message._id}
                      className={`chat-message-row${
                        own ? " own" : ""
                      }${isSystem ? " system" : ""}`}
                    >
                      {!own && !isSystem && (
                        <Avatar
                          avatar={message.sender?.avatar}
                          nickname={message.sender?.nickname || "U"}
                          size={30}
                        />
                      )}
                      <div className="chat-message-bubble">
                        {!isSystem && (
                          <span className="chat-message-sender">
                            {own
                              ? t("chat.you")
                              : message.sender?.nickname || t("chat.unknownUser")}
                          </span>
                        )}
                        <p>
                          {isSystem
                            ? interpolateSystemMessage(
                                message.content,
                                message.data,
                                t
                              )
                            : message.content}
                        </p>
                        <span className="chat-message-time">
                          {getMessageTime(message.timestamp)}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="chat-empty-state">
                  <h4>{t("chat.noMessagesYet")}</h4>
                  <p>{t("chat.startConversation")}</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="chat-composer">
              <textarea
                value={messageText}
                onChange={handleMessageChange}
                onKeyDown={handleComposerKeyDown}
                placeholder={t("chat.typeMessage")}
                maxLength={MAX_MESSAGE_LENGTH}
                disabled={sending || !activeChat}
              />
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={
                  sending ||
                  !activeChat ||
                  !messageText.trim() ||
                  messageText.length > MAX_MESSAGE_LENGTH
                }
              >
                {sending ? t("chat.sending") : t("chat.send")}
              </button>
              {messageText.length > MAX_MESSAGE_LENGTH * 0.8 && (
                <span
                  className={`chat-character-count${
                    messageText.length >= MAX_MESSAGE_LENGTH ? " limit" : ""
                  }`}
                >
                  {messageText.length}/{MAX_MESSAGE_LENGTH}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );

    return (
      <div className="chat-modal-overlay" onClick={handleOverlayClick}>
        <section className="chat-modal" onClick={handlePanelClick}>
          <header className="chat-header">
            <div className="chat-header-title">
              {view === "thread" && (
                <button
                  type="button"
                  className="chat-back-button"
                  onClick={handleBackToList}
                  aria-label={t("chat.backToChats")}
                  title={t("chat.backToChats")}
                >
                  ‹
                </button>
              )}
              {view === "thread" && activeParticipant ? (
                <Avatar
                  avatar={activeParticipant.avatar}
                  nickname={activeParticipant.nickname}
                  size={36}
                />
              ) : (
                <img
                  src={`${process.env.PUBLIC_URL}/images/send.png`}
                  alt=""
                  className="chat-header-icon"
                  aria-hidden="true"
                />
              )}
              <div>
                <h3>
                  {view === "thread"
                    ? activeParticipant?.nickname || t("chat.messages")
                    : t("chat.chats")}
                </h3>
                {view === "thread" && activeParticipant?.fullname && (
                  <span>{activeParticipant.fullname}</span>
                )}
              </div>
            </div>
            <div className="chat-header-actions">
              {view === "list" && (
                <button
                  type="button"
                  className="chat-refresh-button"
                  onClick={refreshChats}
                  disabled={loading}
                >
                  {t("chat.refresh")}
                </button>
              )}
              <button
                type="button"
                className="chat-close-button"
                onClick={onClose}
                aria-label={t("common.close")}
              >
                ×
              </button>
            </div>
          </header>
          {view === "list" ? renderList() : renderThread()}
        </section>
      </div>
    );
  }
);

ChatModal.displayName = "ChatModal";

export default ChatModal;
